import React from 'react';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import '@progress/kendo-theme-fluent/dist/all.css';
import { FaPlusCircle, FaEye, FaCopy } from 'react-icons/fa';
import { Form } from '../../data/formData';
import { useNavigate } from 'react-router-dom';

interface FormsGridProps {
  data: Form[];
  onAdd?: () => void;
  onEdit?: (form: Form) => void;
  onDuplicate?: (form: Form) => void;
}

function FormsGrid({ data, onAdd, onEdit, onDuplicate }: FormsGridProps) {
  const navigate = useNavigate();
  
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const handleRowClick = (event: any) => {
    // Only trigger edit if not clicking on the Responses button
    if (event.nativeEvent.target.tagName !== 'BUTTON' && 
        !event.nativeEvent.target.closest('button')) {
      if (onEdit) {
        onEdit(event.dataItem);
      }
    }
  };

  const ResponsesCell = (props: any) => {
    const formId = props.dataItem.formId;
    
    return (
      <td style={{ textAlign: 'center' }}>
        <Button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/management/forms/responses/${formId}`);
          }}
          fillMode="outline"
          size="small"
        >
          <FaEye style={{ marginRight: '0.3rem' }} />
          View
        </Button>
      </td>
    );
  };

  const DuplicateCell = (props: any) => (
    <td style={{ textAlign: 'center' }}>
      <Button
        onClick={(e) => {
          // The row itself opens the form for editing, so the button has to
          // keep its click to itself.
          e.stopPropagation();
          onDuplicate?.(props.dataItem);
        }}
        fillMode="outline"
        size="small"
        title="Make a copy of this form"
      >
        <FaCopy style={{ marginRight: '0.3rem' }} />
        Duplicate
      </Button>
    </td>
  );

  return (
    <div className="jobs-kendo-fluent-theme">
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        marginBottom: '24px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '4px',
        border: '1px solid #edebe9'
      }}>
        <h2 style={{ 
          fontSize: '1.25rem', 
          fontWeight: '600',
          margin: 0,
          color: '#323130'
        }}>
          Forms Management
        </h2>
      </div>

      {/* Action Buttons */}
      {onAdd && (
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          marginBottom: "1rem"
        }}>
          <div style={{ display: "flex", gap: "1rem" }}>
            <Button
              themeColor="primary"
              onClick={onAdd}
            >
              <FaPlusCircle style={{ marginRight: "0.5rem" }} />
              Add New Form
            </Button>
          </div>
        </div>
      )}

      {/* Kendo Grid */}
      <Grid
        data={data}
        pageable={{
          buttonCount: 5,
          info: true,
          type: 'numeric',
          pageSizes: [10, 25, 50, 100],
          previousNext: true
        }}
        sortable={true}
        filterable={true}
        resizable={true}
        reorderable={true}
        style={{ 
          height: '600px',
          cursor: onEdit ? 'pointer' : 'default'
        }}
        onRowClick={handleRowClick}
      >
        <GridColumn 
          field="name" 
          title="Form Name" 
          width="250px"
          filterable={true}
          sortable={true}
        />
        
        <GridColumn 
          field="description" 
          title="Description" 
          width="350px"
          filterable={true}
          sortable={true}
        />
        
        <GridColumn 
          field="sectionCount" 
          title="Sections" 
          width="120px"
          filterable={true}
          sortable={true}
          cell={({ dataItem }) => (
            <td style={{ textAlign: 'center' }}>{dataItem.sectionCount}</td>
          )}
        />
        
        <GridColumn 
          field="questionCount" 
          title="Questions" 
          width="120px"
          filterable={true}
          sortable={true}
          cell={({ dataItem }) => (
            <td style={{ textAlign: 'center' }}>{dataItem.questionCount}</td>
          )}
        />
        
        <GridColumn 
          title="Responses" 
          width="150px"
          filterable={false}
          sortable={false}
          cell={ResponsesCell}
        />

        {onDuplicate && (
          <GridColumn
            title="Duplicate"
            width="150px"
            filterable={false}
            sortable={false}
            cell={DuplicateCell}
          />
        )}
        
        <GridColumn 
          field="createdDate" 
          title="Created Date" 
          width="140px"
          filter="date"
          format="{0:dd/MM/yyyy}"
          sortable={true}
          cell={({ dataItem }) => (
            <td>{formatDate(dataItem.createdDate)}</td>
          )}
        />
        
        <GridColumn 
          field="createdBy" 
          title="Created By" 
          width="150px"
          filterable={true}
          sortable={true}
        />
      </Grid>

      {/* Summary */}
      <div style={{ 
        marginTop: '24px', 
        padding: '16px', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '2px',
        border: '1px solid #edebe9',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ 
          fontSize: '14px',
          fontWeight: '600',
          color: '#323130'
        }}>
          Total Forms: <span style={{ color: '#0078d4' }}>{data.length}</span>
        </div>
      </div>
    </div>
  );
}

export default FormsGrid;
